import React, { useState, useEffect } from 'react';
import { Button, Flex, Typography } from '../common';
import { TYPOGRAPHY } from '../../constants';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../theme/GlobalTheme';
import { Switch } from 'antd';
import { getNotificationSettings, handleApiError, saveNotificationSetting } from '../../api';
import { showMessage } from '../common/Message';

function NotificationSettings() {
  const navigate = useNavigate();
  const { isDark } = useThemeContext();
  const [loading, setLoading] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    notifications: false,
    email: false,
  });

  const fetchSettings = async () => {
    try {
      const response = await getNotificationSettings();
      const settings = response?.data?.data?.[0] || {};
      setNotificationSettings({
        notifications: settings.notifications === true,
        email: settings.email === true,
      });
    } catch (error) {
      const errMsg = handleApiError(error);
      showMessage({ type: 'error', content: errMsg });
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const persistSettings = async (payload) => {
    setLoading(true);
    try {
      await saveNotificationSetting(payload);
      setNotificationSettings({
        notifications: payload.notifications === true,
        email: payload.email === true,
      });
      showMessage({
        type: 'success',
        content: payload.email
          ? 'Notification settings saved successfully!'
          : 'Notification settings saved. Emails for this account are now stopped.',
      });
      return true;
    } catch (error) {
      const errMsg = handleApiError(error);
      showMessage({ type: 'error', content: errMsg });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchChange = async (key, checked) => {
    const previous = notificationSettings;
    const next = { ...previous, [key]: checked };
    setNotificationSettings(next);
    const ok = await persistSettings(next);
    if (!ok) {
      setNotificationSettings(previous);
    }
  };

  const handleSave = async () => {
    await persistSettings(notificationSettings);
  };

  return (
    <Flex
      vertical
      className="h-full min-h-[calc(100vh-12rem)] justify-between"
    >
      <Flex vertical className="pt-10 gap-6">
        <Typography text="Notification Management" type={TYPOGRAPHY.TITLE} />

        <Flex
          className="border-[2px] border-purple-600 rounded-md py-3 px-4 max-w-96"
          justify="space-between"
          align="center"
        >
          <Typography
            text="Notifications"
            type={TYPOGRAPHY.SUBTITLE}
            className="!font-bold"
          />
          <Switch
            checked={notificationSettings.notifications}
            loading={loading}
            onChange={(checked) => handleSwitchChange('notifications', checked)}
          />
        </Flex>

        <Flex
          className="border-[2px] border-purple-600 rounded-md py-3 px-4 max-w-96"
          justify="space-between"
          align="center"
        >
          <Typography
            text="Email Notifications"
            type={TYPOGRAPHY.SUBTITLE}
            className="!font-bold"
          />
          <Switch
            checked={notificationSettings.email}
            loading={loading}
            onChange={(checked) => handleSwitchChange('email', checked)}
          />
        </Flex>
        <Typography
          text="Toggles save immediately. Turning Email Notifications off stops SurpassIn emails for this account."
          type={TYPOGRAPHY.PARA}
        />
      </Flex>
      <Flex vertical justify="space-between" className="w-full text-white">
        <Flex
          justify="end"
          className={`sticky bottom-0 w-full lg:justify-between justify-end p-4 border-t h-[56px] md:h-[72px] border-[#7C30F7] ${
            isDark ? 'bg-primaryDark' : 'bg-primaryLight'
          }`}
        >
          <Button
            text="Back"
            onClick={() => navigate(-1)}
            buttonProps={{ type: 'primary-none' }}
          />
          <Button
            text="Save"
            buttonProps={{ type: 'primary-square' }}
            loadingData={loading}
            onClick={handleSave}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}

export default NotificationSettings;
